import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { FileOpener } from '@capacitor-community/file-opener';
import { Printer } from '@capgo/capacitor-printer';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/runtime';

/**
 * Downloads, "open in new tab" and printing, made to work inside the store app.
 *
 * The ERP produces files in ~35 places, nearly all the same way: build a blob,
 * point an <a download> at it and click it, or window.open() it. A phone's
 * WebView does nothing with either — no download manager, no second tab — so
 * every export, PDF and label sheet would fail silently. Rather than touch each
 * call site (and miss the next one somebody writes), the app patches those
 * three entry points once, in installNativeFileShims(), and only in the app:
 * a browser never runs any of this.
 *
 * A file is written to the app's cache and handed to the phone — opened in its
 * own viewer (which brings print, share and save with it), falling back to the
 * share sheet when nothing on the phone can open that type.
 */

const MIME_BY_EXT = {
  pdf: 'application/pdf',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
  csv: 'text/csv',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  zip: 'application/zip',
  txt: 'text/plain',
  html: 'text/html',
  json: 'application/json',
};
const EXT_BY_MIME = Object.fromEntries(Object.entries(MIME_BY_EXT).map(([e, m]) => [m, e]));

// Blobs by object URL. Most call sites revoke the URL straight after click(),
// before an async native save could read it, so in the app the blob is kept
// here and the revoke is deferred.
const blobs = new Map();

function extOf(name) {
  const m = /\.([a-z0-9]{2,5})$/i.exec(name || '');
  return m ? m[1].toLowerCase() : '';
}

function cleanName(name, mime) {
  let n = (name || '').split(/[\\/]/).pop().replace(/[:*?"<>|]+/g, '_').trim() || 'document';
  if (!extOf(n) && EXT_BY_MIME[mime]) n = `${n}.${EXT_BY_MIME[mime]}`;
  return n;
}

function mimeFor(blob, name) {
  const t = (blob?.type || '').split(';')[0];
  if (t && t !== 'application/octet-stream') return t;
  return MIME_BY_EXT[extOf(name)] || 'application/octet-stream';
}

function nameFromUrl(url) {
  try {
    const u = new URL(url, window.location.href);
    return decodeURIComponent(u.pathname.split('/').pop() || '');
  } catch {
    return '';
  }
}

function nameFromDisposition(header) {
  if (!header) return '';
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star) return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : '';
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/** Write a blob to the cache and open it on the phone (or share it). */
export async function deliverBlob(blob, filename, { mode = 'open' } = {}) {
  const mime = mimeFor(blob, filename);
  const name = cleanName(filename, mime);
  try {
    const { uri } = await Filesystem.writeFile({
      path: `exports/${name}`,
      data: await blobToBase64(blob),
      directory: Directory.Cache,
      recursive: true,
    });
    if (mode === 'open') {
      try {
        await FileOpener.open({ filePath: uri, contentType: mime, openWithDefault: true });
        return;
      } catch {
        /* nothing on the phone opens this type — offer it to share instead */
      }
    }
    await Share.share({ title: name, files: [uri], dialogTitle: name });
  } catch (err) {
    // Cancelling the share sheet is not a failure.
    if (/cancel/i.test(String(err?.message || err))) return;
    toast.error(`Could not open ${name}`);
  }
}

function isBackendUrl(url) {
  if (!BACKEND_URL) return false;
  try {
    return new URL(url, window.location.href).origin === new URL(BACKEND_URL).origin;
  } catch {
    return false;
  }
}

/** Fetch a workspace file with the session's token and open it. */
async function deliverUrl(url, filename) {
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const name = filename || nameFromDisposition(res.headers.get('Content-Disposition')) || nameFromUrl(url);
    await deliverBlob(blob, name);
  } catch {
    toast.error('Could not download the file');
  }
}

/** Route one URL the page wanted opened or downloaded. Returns false to let it through. */
function handleUrl(url, filename) {
  if (!url) return false;
  if (url.startsWith('blob:')) {
    const blob = blobs.get(url);
    if (blob) {
      deliverBlob(blob, filename || 'document');
    } else {
      // Not one of ours (created before the shim) — still readable while unrevoked.
      fetch(url).then((r) => r.blob()).then((b) => deliverBlob(b, filename || 'document'))
        .catch(() => toast.error('Could not open the file'));
    }
    return true;
  }
  if (url.startsWith('data:')) {
    fetch(url).then((r) => r.blob()).then((b) => deliverBlob(b, filename || 'document'))
      .catch(() => toast.error('Could not open the file'));
    return true;
  }
  if (isBackendUrl(url)) {
    deliverUrl(url, filename);
    return true;
  }
  if (/^https?:\/\//i.test(url)) {
    Browser.open({ url }).catch(() => {});
    return true;
  }
  return false;
}

function anchorWantsHandling(a) {
  const href = a.getAttribute('href') || '';
  if (!href || href.startsWith('#') || /^(mailto|tel|sms|whatsapp|geo|intent):/i.test(href)) return false;
  if (href.startsWith('blob:') || href.startsWith('data:') || a.hasAttribute('download')) return true;
  // A plain in-app link (router) is left alone; only "new tab" ones are taken.
  return a.target === '_blank';
}

function waitForForeground(ms) {
  // Android shows the print dialog as its own screen, so the app is paused
  // while the operator chooses a printer. Wait for it to come back before
  // telling the page printing is over — tearing the print copy down early
  // would blank the preview the operator is looking at.
  return new Promise((resolve) => {
    let left = false;
    let sub;
    const finish = () => { if (sub) sub.then((h) => h.remove()).catch(() => {}); resolve(); };
    sub = CapApp.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) left = true;
      else if (left) finish();
    });
    setTimeout(() => { if (!left) finish(); }, ms);
  });
}

async function nativePrint() {
  window.dispatchEvent(new Event('beforeprint'));
  try {
    await Printer.printWebView({ name: document.title || 'Aqua ERP' });
    await waitForForeground(1500);
  } catch (err) {
    if (!/cancel/i.test(String(err?.message || err))) toast.error('Printing is not available on this phone');
  } finally {
    window.dispatchEvent(new Event('afterprint'));
  }
}

let installed = false;

export function installNativeFileShims() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const createObjectURL = URL.createObjectURL.bind(URL);
  const revokeObjectURL = URL.revokeObjectURL.bind(URL);
  URL.createObjectURL = (obj) => {
    const url = createObjectURL(obj);
    if (obj instanceof Blob) blobs.set(url, obj);
    return url;
  };
  URL.revokeObjectURL = (url) => {
    setTimeout(() => { blobs.delete(url); revokeObjectURL(url); }, 120000);
  };

  // Programmatic downloads: const a = document.createElement('a'); …; a.click()
  const click = HTMLAnchorElement.prototype.click;
  HTMLAnchorElement.prototype.click = function patchedClick() {
    if (anchorWantsHandling(this) && handleUrl(this.href, this.getAttribute('download') || '')) return;
    click.call(this);
  };

  // Links the operator taps: "Open PDF", <a download>, target=_blank.
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0) return;
    const a = e.target?.closest?.('a[href]');
    if (!a || !anchorWantsHandling(a)) return;
    if (handleUrl(a.href, a.getAttribute('download') || '')) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, true);

  const open = window.open.bind(window);
  window.open = (url, target, features) => {
    const href = url ? new URL(String(url), window.location.href).href : '';
    // An in-app path ("/procurement-batches") opens in place — there is no
    // second tab — via the router, without reloading the app.
    if (url && String(url).startsWith('/') && !isBackendUrl(href)) {
      window.history.pushState({}, '', String(url));
      window.dispatchEvent(new PopStateEvent('popstate'));
      return { closed: false, close() {}, focus() {} };
    }
    if (handleUrl(href, '')) {
      // Callers test the return value and fall back to a second download when
      // it is null, so report success.
      return { closed: false, close() {}, focus() {} };
    }
    return open(url, target, features);
  };

  window.print = () => { nativePrint(); };
}
